<template>
  <div class="page financeiro-page">
    <div class="page-header">
      <div class="page-header-row">
        <div>
          <h1 class="page-title">Financeiro</h1>
          <p class="page-subtitle">Controle de entradas, saídas e fluxo de caixa</p>
        </div>
        <button
          v-if="activeTab === 'extrato'"
          type="button"
          class="btn btn-secondary btn-import-ofx"
          @click="showOfxWizard = true"
        >
          <i class="material-icons">upload_file</i>
          Importar OFX
        </button>
      </div>
    </div>

    <div class="financeiro-tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="financeiro-tab"
        :class="{ 'financeiro-tab--active': activeTab === tab.id }"
        :aria-selected="activeTab === tab.id"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Extrato -->
    <section v-if="activeTab === 'extrato'" class="financeiro-section">
      <div class="financeiro-filters">
        <v-select
          :options="periodOptions"
          :reduce="(o) => o.id"
          v-model="periodRange"
          placeholder="Período"
          :clearable="false"
        />
        <v-select
          :options="accountFilterOptions"
          :reduce="(o) => o.id"
          v-model="accountFilter"
          placeholder="Conta"
          :clearable="false"
        />
        <v-select
          :options="typeFilterOptions"
          :reduce="(o) => o.id"
          v-model="typeFilter"
          placeholder="Tipo"
          :clearable="false"
        />
        <v-select
          :options="categoryFilterOptions"
          :reduce="(o) => o.id"
          v-model="categoryFilter"
          placeholder="Categoria"
          :clearable="false"
        />
        <template v-if="periodRange === 'custom'">
          <input v-model="customStartDate" type="date" class="financeiro-date-field" aria-label="Data inicial" />
          <input v-model="customEndDate" type="date" class="financeiro-date-field" aria-label="Data final" />
        </template>
      </div>

      <div class="financeiro-kpis card-list">
        <article class="card card--with-icon financeiro-kpi">
          <div class="card-icon-wrap financeiro-kpi-in">
            <i class="material-icons">arrow_downward</i>
          </div>
          <div class="card-content">
            <span class="financeiro-kpi-value">{{ formatBrl(periodSummary.inflows) }}</span>
            <p class="card-meta card-secondary">Entradas</p>
          </div>
        </article>
        <article class="card card--with-icon financeiro-kpi">
          <div class="card-icon-wrap financeiro-kpi-out">
            <i class="material-icons">arrow_upward</i>
          </div>
          <div class="card-content">
            <span class="financeiro-kpi-value">{{ formatBrl(periodSummary.outflows) }}</span>
            <p class="card-meta card-secondary">Saídas</p>
          </div>
        </article>
        <article class="card card--with-icon financeiro-kpi">
          <div class="card-icon-wrap financeiro-kpi-net">
            <i class="material-icons">account_balance_wallet</i>
          </div>
          <div class="card-content">
            <span class="financeiro-kpi-value">{{ formatBrl(periodSummary.net) }}</span>
            <p class="card-meta card-secondary">Líquido</p>
          </div>
        </article>
      </div>

      <div v-if="filteredTransactions.length === 0" class="empty-hint">
        Nenhuma transação no período.
      </div>

      <div v-else class="financeiro-tx-list">
        <article
          v-for="tx in filteredTransactions"
          :key="tx.id"
          class="card card--with-icon financeiro-tx-card"
        >
          <div class="card-icon-wrap" :class="tx.amount >= 0 ? 'financeiro-tx-in' : 'financeiro-tx-out'">
            <i class="material-icons">{{ tx.amount >= 0 ? 'south_west' : 'north_east' }}</i>
          </div>
          <div class="card-content">
            <h3 class="card-title card-primary financeiro-tx-desc">{{ tx.description }}</h3>
            <p class="card-meta card-secondary">
              {{ formatDateMs(tx.date) }} · {{ getAccountName(tx.accountId) }}
              · {{ getCategoryLabel(tx.categoryId) }}
              <span v-if="tx.source === 'ofx'" class="financeiro-source-badge">OFX</span>
            </p>
          </div>
          <div class="financeiro-tx-amount" :class="tx.amount >= 0 ? 'amount-in' : 'amount-out'">
            {{ formatBrl(tx.amount) }}
          </div>
          <div class="card-actions financeiro-tx-actions">
            <button type="button" class="btn-icon" aria-label="Editar" @click="openEditTransaction(tx)">
              <i class="material-icons">edit</i>
            </button>
            <button type="button" class="btn-icon btn-icon-danger" aria-label="Excluir" @click="handleDeleteTransaction(tx)">
              <i class="material-icons">delete_outline</i>
            </button>
          </div>
        </article>
      </div>

      <button type="button" class="btn-fab" aria-label="Nova transação" @click="openAddTransaction">
        <i class="material-icons">add</i>
      </button>
    </section>

    <!-- Fluxo de caixa -->
    <section v-else-if="activeTab === 'fluxo'" class="financeiro-section">
      <div class="financeiro-filters">
        <v-select
          :options="periodOptions"
          :reduce="(o) => o.id"
          v-model="cashflowPeriodRange"
          placeholder="Período"
          :clearable="false"
        />
        <v-select
          :options="accountFilterOptions"
          :reduce="(o) => o.id"
          v-model="cashflowAccountFilter"
          placeholder="Conta"
          :clearable="false"
        />
        <div class="financeiro-toggle-group">
          <button
            type="button"
            class="financeiro-toggle-btn"
            :class="{ 'financeiro-toggle-btn--active': cashflowGranularity === 'day' }"
            @click="cashflowGranularity = 'day'"
          >
            Por dia
          </button>
          <button
            type="button"
            class="financeiro-toggle-btn"
            :class="{ 'financeiro-toggle-btn--active': cashflowGranularity === 'month' }"
            @click="cashflowGranularity = 'month'"
          >
            Por mês
          </button>
        </div>
        <div class="financeiro-toggle-group">
          <button
            type="button"
            class="financeiro-toggle-btn"
            :class="{ 'financeiro-toggle-btn--active': balanceView === 'consolidated' }"
            @click="balanceView = 'consolidated'"
          >
            Consolidado
          </button>
          <button
            type="button"
            class="financeiro-toggle-btn"
            :class="{ 'financeiro-toggle-btn--active': balanceView === 'account' }"
            @click="balanceView = 'account'"
          >
            Por conta
          </button>
        </div>
      </div>

      <div v-if="chartReady" class="financeiro-chart-wrap card card--panel">
        <div class="financeiro-chart-container">
          <apexchart width="100%" type="bar" :series="cashflowSeries" :options="cashflowChartOptions" />
        </div>
      </div>

      <div v-if="runningBalancePoints.length > 0" class="financeiro-running-balance card card--panel">
        <h2 class="financeiro-subheading">
          Saldo acumulado
          <template v-if="balanceView === 'account' && selectedCashflowAccount">
            — {{ selectedCashflowAccount.name }}
          </template>
          <template v-else>(consolidado)</template>
        </h2>
        <div class="financeiro-chart-container">
          <apexchart width="100%" type="line" :series="runningBalanceSeries" :options="runningBalanceChartOptions" />
        </div>
      </div>

      <div class="financeiro-summary-table-wrap card card--panel">
        <table class="financeiro-summary-table">
          <thead>
            <tr>
              <th>{{ cashflowGranularity === 'day' ? 'Data' : 'Mês' }}</th>
              <th class="num">Entradas</th>
              <th class="num">Saídas</th>
              <th class="num">Líquido</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in cashflowRows" :key="row.key">
              <td>{{ row.label }}</td>
              <td class="num amount-in">{{ formatBrl(row.inflows) }}</td>
              <td class="num amount-out">{{ formatBrl(row.outflows) }}</td>
              <td class="num">{{ formatBrl(row.net) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Resultado -->
    <FinanceiroResultadoTab
      v-else-if="activeTab === 'resultado'"
      :accounts="accounts"
      :all-transactions="allTransactions"
      :categories="categories"
      :filter-by-date-range="filterByDateRange"
      :to-date-key="toDateKey"
    />

    <!-- Categorias -->
    <FinanceiroCategoriasTab v-else-if="activeTab === 'categorias'" />

    <!-- Contas -->
    <section v-else-if="activeTab === 'contas'" class="financeiro-section">
      <div class="card-list">
        <article
          v-for="account in accounts"
          :key="account.id"
          class="card card--with-icon"
        >
          <div class="card-icon-wrap financeiro-account-icon">
            <i class="material-icons">{{ accountTypeIcon(account.type) }}</i>
          </div>
          <div class="card-content">
            <h3 class="card-title card-primary">{{ account.name }}</h3>
            <p class="card-meta card-secondary">
              {{ accountTypeLabel(account.type) }}
              <span v-if="account.institution"> · {{ account.institution }}</span>
            </p>
            <p class="card-meta card-accent financeiro-account-balance">
              Saldo: {{ formatBrl(accountBalance(account.id)) }}
            </p>
          </div>
          <div class="card-actions">
            <button type="button" class="btn-icon" aria-label="Editar" @click="openEditAccount(account)">
              <i class="material-icons">edit</i>
            </button>
            <button type="button" class="btn-icon btn-icon-danger" aria-label="Excluir" @click="handleDeleteAccount(account)">
              <i class="material-icons">delete_outline</i>
            </button>
          </div>
        </article>
      </div>

      <p v-if="accounts.length === 0" class="empty-hint">
        Nenhuma conta cadastrada. Adicione a primeira.
      </p>

      <button type="button" class="btn-fab" aria-label="Nova conta" @click="openAddAccount">
        <i class="material-icons">add</i>
      </button>
    </section>

    <!-- Account drawer -->
    <Drawer :model-value="showAccountModal" @update:model-value="showAccountModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingAccountId ? 'Alterar conta' : 'Nova conta' }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showAccountModal = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveAccount">
          <div class="form-group">
            <label for="account-name">Nome</label>
            <input id="account-name" v-model="accountNameInput" type="text" placeholder="Ex: Nubank PJ" />
            <p v-if="accountNameError" class="field-error" role="alert">{{ accountNameError }}</p>
          </div>
          <div class="form-group">
            <label for="account-type">Tipo</label>
            <v-select
              id="account-type"
              :options="accountTypeOptions"
              :reduce="(o) => o.id"
              v-model="accountType"
              label="label"
              placeholder="Tipo de conta"
              :clearable="false"
            />
          </div>
          <div class="form-group">
            <label for="account-institution">Instituição (opcional)</label>
            <input id="account-institution" v-model="accountInstitution" type="text" placeholder="Ex: Nubank" />
          </div>
          <div class="form-group">
            <label for="account-last-four">Últimos 4 dígitos (opcional)</label>
            <input id="account-last-four" v-model="accountLastFour" type="text" maxlength="4" />
          </div>
          <div class="form-group">
            <label for="account-opening-balance">Saldo inicial</label>
            <CurrencyInput id="account-opening-balance" v-model="accountOpeningBalance" aria-label="Saldo inicial" />
            <p v-if="accountOpeningBalanceError" class="field-error" role="alert">{{ accountOpeningBalanceError }}</p>
          </div>
          <div class="form-group">
            <label for="account-opening-date">Data do saldo inicial</label>
            <input id="account-opening-date" v-model="accountOpeningDate" type="date" />
            <p v-if="accountOpeningDateError" class="field-error" role="alert">{{ accountOpeningDateError }}</p>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="accountSaveLoading" @click="showAccountModal = false">Cancelar</button>
        <button type="button" class="btn btn-primary" :disabled="accountSaveLoading" @click="saveAccount">
          {{ accountSaveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <!-- Transaction drawer -->
    <Drawer :model-value="showTransactionModal" @update:model-value="showTransactionModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingTransactionId ? 'Alterar transação' : 'Nova transação' }}</h2>
        <button type="button" class="modal-close" aria-label="Fechar" @click="showTransactionModal = false">
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveTransaction">
          <div class="form-group">
            <label for="tx-account">Conta</label>
            <v-select
              id="tx-account"
              :options="accountOptions"
              :reduce="(o) => o.id"
              v-model="txAccountId"
              label="label"
              placeholder="Selecione a conta"
              :clearable="false"
              :disabled="accountOptions.length === 0"
            />
            <p v-if="accountOptions.length === 0" class="field-hint">
              Cadastre uma conta na aba Contas antes de adicionar transações.
            </p>
            <p v-if="txAccountError" class="field-error" role="alert">{{ txAccountError }}</p>
          </div>
          <div class="form-group">
            <label for="tx-date">Data</label>
            <input id="tx-date" v-model="txDate" type="date" />
            <p v-if="txDateError" class="field-error" role="alert">{{ txDateError }}</p>
          </div>
          <div class="form-group">
            <label>Tipo</label>
            <div class="financeiro-type-toggle">
              <button
                type="button"
                class="financeiro-toggle-btn"
                :class="{ 'financeiro-toggle-btn--active': txDirection === 'in' }"
                @click="setTxDirection('in')"
              >
                Entrada
              </button>
              <button
                type="button"
                class="financeiro-toggle-btn"
                :class="{ 'financeiro-toggle-btn--active': txDirection === 'out' }"
                @click="setTxDirection('out')"
              >
                Saída
              </button>
            </div>
          </div>
          <div class="form-group">
            <label for="tx-category">Categoria (opcional)</label>
            <v-select
              id="tx-category"
              :options="txCategoryOptions"
              :reduce="(o) => o.id"
              v-model="txCategoryId"
              label="label"
              placeholder="Nenhuma"
              :clearable="false"
              @update:model-value="onCategorySelected"
            />
          </div>
          <div class="form-group">
            <label for="tx-amount">Valor</label>
            <CurrencyInput id="tx-amount" v-model="txAmount" aria-label="Valor" />
            <p v-if="txAmountError" class="field-error" role="alert">{{ txAmountError }}</p>
          </div>
          <div class="form-group">
            <label for="tx-description">Descrição</label>
            <input id="tx-description" v-model="txDescription" type="text" placeholder="Ex: Aluguel, Pix recebido" />
            <p v-if="txDescriptionError" class="field-error" role="alert">{{ txDescriptionError }}</p>
          </div>
          <div class="form-group">
            <label for="tx-notes">Observações (opcional)</label>
            <textarea id="tx-notes" v-model="txNotes" rows="2" />
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="txSaveLoading" @click="showTransactionModal = false">Cancelar</button>
        <button type="button" class="btn btn-primary" :disabled="txSaveLoading" @click="saveTransaction">
          {{ txSaveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteTxConfirm"
      title="Excluir transação"
      :message="deleteTxMessage"
      confirm-label="Excluir"
      :confirm-loading="deleteTxLoading"
      @confirm="doDeleteTransaction"
    />

    <ConfirmDialog
      v-model="showDeleteAccountConfirm"
      title="Excluir conta"
      :message="deleteAccountMessage"
      confirm-label="Excluir tudo"
      :confirm-loading="deleteAccountLoading"
      @confirm="doDeleteAccount"
    />

    <OfxImportWizard
      v-model="showOfxWizard"
      :accounts="accounts"
      :existing-transactions="allTransactions"
      :import-loading="ofxImportLoading"
      @import="handleOfxImport"
    />
  </div>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import OfxImportWizard from '@/components/financeiro/OfxImportWizard.vue';
import FinanceiroCategoriasTab from '@/components/financeiro/FinanceiroCategoriasTab.vue';
import FinanceiroResultadoTab from '@/components/financeiro/FinanceiroResultadoTab.vue';
import VueApexCharts from 'vue3-apexcharts';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch } from 'vue';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firestore, app } from '@/firebase';
import { auth } from '@/firebase';
import { useToast } from '@/composables/useToast';
import { useBankAccountStore } from '@/stores/bankAccount';
import { useBankTransactionStore } from '@/stores/bankTransaction';
import { useTransactionCategoryStore } from '@/stores/transactionCategory';
import { UNCATEGORIZED_ID, UNCATEGORIZED_LABEL } from '@/constants/transactionCategoryKinds';
import { useBankBalances } from '@/composables/useBankBalances';
import { BANK_ACCOUNT_TYPES, BANK_ACCOUNT_TYPE_LABELS, getBankAccountTypeIcon } from '@/constants/bankAccountTypes';
import { roundMoney } from '@/composables/usePaymentMethodCalculations';
import { startOfLocalDay } from '@/utils/ofxParser';

const PERIOD_MS = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export default {
  name: 'Financeiro',
  components: {
    Drawer,
    ConfirmDialog,
    CurrencyInput,
    OfxImportWizard,
    FinanceiroCategoriasTab,
    FinanceiroResultadoTab,
    apexchart: VueApexCharts,
    vSelect,
  },
  setup() {
    const { show: showToast } = useToast();
    const bankAccountStore = useBankAccountStore();
    const bankTransactionStore = useBankTransactionStore();
    const transactionCategoryStore = useTransactionCategoryStore();

    const accounts = computed(() => bankAccountStore.allBankAccounts);
    const allTransactions = computed(() => bankTransactionStore.allBankTransactions);
    const categories = computed(() => transactionCategoryStore.allTransactionCategories);
    const balances = useBankBalances(accounts, allTransactions);

    const tabs = [
      { id: 'extrato', label: 'Extrato' },
      { id: 'fluxo', label: 'Fluxo de caixa' },
      { id: 'resultado', label: 'Resultado' },
      { id: 'contas', label: 'Contas' },
      { id: 'categorias', label: 'Categorias' },
    ];
    const activeTab = ref('extrato');

    const periodOptions = [
      { id: 'today', label: 'Hoje' },
      { id: 'month', label: 'Este mês' },
      { id: '30d', label: 'Últimos 30 dias' },
      { id: '90d', label: 'Últimos 90 dias' },
      { id: 'year', label: 'Este ano' },
      { id: 'custom', label: 'Personalizado' },
    ];
    const typeFilterOptions = [
      { id: 'all', label: 'Todas' },
      { id: 'in', label: 'Entradas' },
      { id: 'out', label: 'Saídas' },
    ];

    const periodRange = ref('month');
    const cashflowPeriodRange = ref('year');
    const customStartDate = ref('');
    const customEndDate = ref('');
    const accountFilter = ref('all');
    const cashflowAccountFilter = ref('all');
    const typeFilter = ref('all');
    const categoryFilter = ref('all');
    const cashflowGranularity = ref('month');
    const balanceView = ref('consolidated');
    const chartReady = ref(true);

    const accountFilterOptions = computed(() => [
      { id: 'all', label: 'Todas as contas' },
      ...accounts.value.map((a) => ({ id: a.id, label: a.name })),
    ]);

    const accountOptions = computed(() =>
      accounts.value.map((a) => ({ id: a.id, label: a.name }))
    );
    const accountTypeOptions = BANK_ACCOUNT_TYPES;

    const categoryFilterOptions = computed(() => [
      { id: 'all', label: 'Todas categorias' },
      { id: UNCATEGORIZED_ID, label: UNCATEGORIZED_LABEL },
      ...categories.value.map((c) => ({ id: c.id, label: c.name })),
    ]);

    function getDateRange(preset) {
      const now = new Date();
      const nowMs = now.getTime();
      if (preset === 'custom') {
        const start = customStartDate.value ? new Date(customStartDate.value + 'T00:00:00').getTime() : nowMs - PERIOD_MS['30d'];
        const end = customEndDate.value ? new Date(customEndDate.value + 'T23:59:59').getTime() : nowMs;
        return { min: start, max: end };
      }
      if (preset === 'year') {
        return { min: new Date(now.getFullYear(), 0, 1).getTime(), max: nowMs };
      }
      if (preset === 'month') {
        return { min: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), max: nowMs };
      }
      if (preset === 'today') {
        return { min: new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(), max: nowMs };
      }
      const ms = PERIOD_MS[preset];
      return { min: nowMs - (ms || PERIOD_MS['30d']), max: nowMs };
    }

    function filterTransactions(range, accountId, type, categoryId = 'all') {
      const { min, max } = getDateRange(range);
      let list = balances.filterByDateRange(allTransactions.value, min, max);
      if (accountId && accountId !== 'all') {
        list = list.filter((tx) => tx.accountId === accountId);
      }
      if (type === 'in') list = list.filter((tx) => Number(tx.amount) > 0);
      if (type === 'out') list = list.filter((tx) => Number(tx.amount) < 0);
      if (categoryId === UNCATEGORIZED_ID) {
        list = list.filter((tx) => !tx.categoryId);
      } else if (categoryId && categoryId !== 'all') {
        list = list.filter((tx) => tx.categoryId === categoryId);
      }
      return list.sort((a, b) => (Number(b.date) || 0) - (Number(a.date) || 0));
    }

    const filteredTransactions = computed(() =>
      filterTransactions(
        periodRange.value,
        accountFilter.value,
        typeFilter.value,
        categoryFilter.value
      )
    );

    const periodSummary = computed(() => balances.summarizeTransactions(filteredTransactions.value));

    const cashflowTransactions = computed(() =>
      filterTransactions(cashflowPeriodRange.value, cashflowAccountFilter.value, 'all')
    );

    const cashflowRows = computed(() => {
      const rows = cashflowGranularity.value === 'day'
        ? balances.aggregateByDay(cashflowTransactions.value)
        : balances.aggregateByMonth(cashflowTransactions.value);
      return rows.map((r) => ({
        ...r,
        key: r.dateKey || r.monthKey,
        label: r.dateLabel || r.monthLabel,
      }));
    });

    const cashflowSeries = computed(() => [
      { name: 'Entradas', data: cashflowRows.value.map((r) => r.inflows) },
      { name: 'Saídas', data: cashflowRows.value.map((r) => r.outflows) },
    ]);

    const cashflowChartOptions = computed(() => ({
      chart: { toolbar: { show: false }, fontFamily: 'var(--font-sans)', parentHeightOffset: 0 },
      xaxis: {
        categories: cashflowRows.value.map((r) => r.label),
        labels: {
          rotate: -45,
          trim: true,
          hideOverlappingLabels: true,
          style: { fontSize: '11px' },
        },
      },
      colors: ['#5a8f5a', '#b07d7d'],
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      dataLabels: { enabled: false },
      legend: { position: 'top' },
      grid: { padding: { left: 8, right: 8 } },
      yaxis: {
        labels: {
          formatter: (v) => formatBrl(v),
          maxWidth: 72,
          style: { fontSize: '11px' },
        },
      },
      tooltip: { y: { formatter: (v) => formatBrl(v) } },
    }));

    const selectedCashflowAccount = computed(() =>
      accounts.value.find((a) => a.id === cashflowAccountFilter.value) || null
    );

    const cashflowDateRange = computed(() => getDateRange(cashflowPeriodRange.value));

    const runningBalancePoints = computed(() => {
      const { min, max } = cashflowDateRange.value;
      const mode = cashflowGranularity.value;
      if (balanceView.value === 'account') {
        const account = selectedCashflowAccount.value;
        if (!account) return [];
        return balances.accountRunningBalance(mode, min, max, account.id);
      }
      return balances.consolidatedRunningBalance(mode, min, max);
    });

    const runningBalanceSeries = computed(() => [{
      name: 'Saldo',
      data: runningBalancePoints.value.map((p) => p.balance),
    }]);

    const runningBalanceChartOptions = computed(() => ({
      chart: { toolbar: { show: false }, fontFamily: 'var(--font-sans)', parentHeightOffset: 0 },
      xaxis: {
        categories: runningBalancePoints.value.map((p) => p.label),
        labels: {
          rotate: -45,
          trim: true,
          hideOverlappingLabels: true,
          style: { fontSize: '11px' },
        },
      },
      colors: ['#5a5a5a'],
      stroke: { curve: 'smooth' },
      dataLabels: { enabled: false },
      grid: { padding: { left: 8, right: 8 } },
      yaxis: {
        labels: {
          formatter: (v) => formatBrl(v),
          maxWidth: 72,
          style: { fontSize: '11px' },
        },
      },
      tooltip: { y: { formatter: (v) => formatBrl(v) } },
    }));

    watch(periodRange, (newVal, oldVal) => {
      if (newVal === 'custom' && oldVal && oldVal !== 'custom') {
        const range = getDateRange(oldVal);
        customStartDate.value = balances.toDateKey(range.min);
        customEndDate.value = balances.toDateKey(range.max);
      }
    });

    // Account form
    const showAccountModal = ref(false);
    const editingAccountId = ref(null);
    const accountNameInput = ref('');
    const accountType = ref('checking');
    const accountInstitution = ref('');
    const accountLastFour = ref('');
    const accountOpeningBalance = ref(0);
    const accountOpeningDate = ref('');
    const accountNameError = ref('');
    const accountOpeningBalanceError = ref('');
    const accountOpeningDateError = ref('');
    const accountSaveLoading = ref(false);

    function openAddAccount() {
      editingAccountId.value = null;
      accountNameInput.value = '';
      accountType.value = 'checking';
      accountInstitution.value = '';
      accountLastFour.value = '';
      accountOpeningBalance.value = 0;
      const today = new Date();
      accountOpeningDate.value = balances.toDateKey(today.getTime());
      accountNameError.value = '';
      accountOpeningBalanceError.value = '';
      accountOpeningDateError.value = '';
      showAccountModal.value = true;
    }

    function openEditAccount(account) {
      editingAccountId.value = account.id;
      accountNameInput.value = account.name || '';
      accountType.value = account.type || 'checking';
      accountInstitution.value = account.institution || '';
      accountLastFour.value = account.lastFour || '';
      accountOpeningBalance.value = Number(account.openingBalance) || 0;
      accountOpeningDate.value = balances.toDateKey(Number(account.openingBalanceDate) || Date.now());
      showAccountModal.value = true;
    }

    function validateAccount() {
      accountNameError.value = '';
      accountOpeningBalanceError.value = '';
      accountOpeningDateError.value = '';
      let valid = true;
      if (!accountNameInput.value.trim()) {
        accountNameError.value = 'Informe o nome da conta.';
        valid = false;
      }
      if (accountOpeningBalance.value === null || accountOpeningBalance.value === undefined || isNaN(accountOpeningBalance.value)) {
        accountOpeningBalanceError.value = 'Informe o saldo inicial.';
        valid = false;
      }
      if (!accountOpeningDate.value) {
        accountOpeningDateError.value = 'Informe a data do saldo inicial.';
        valid = false;
      }
      return valid;
    }

    async function saveAccount() {
      if (!validateAccount()) return;
      accountSaveLoading.value = true;
      try {
        const id = editingAccountId.value || doc(collection(firestore, 'bankAccounts')).id;
        const now = Date.now();
        const payload = {
          name: accountNameInput.value.trim(),
          type: accountType.value,
          institution: accountInstitution.value.trim() || '',
          lastFour: accountLastFour.value.trim() || '',
          openingBalance: roundMoney(Number(accountOpeningBalance.value) || 0),
          openingBalanceDate: startOfLocalDay(new Date(accountOpeningDate.value + 'T00:00:00').getTime()),
          isActive: true,
          order: editingAccountId.value
            ? (bankAccountStore.bankAccounts[editingAccountId.value]?.order ?? 0)
            : accounts.value.length,
          updatedAt: now,
        };
        if (!editingAccountId.value) payload.createdAt = now;
        await setDoc(doc(firestore, 'bankAccounts', id), payload, { merge: true });
        showAccountModal.value = false;
        showToast(editingAccountId.value ? 'Conta alterada.' : 'Conta criada.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar a conta.', 'error');
      } finally {
        accountSaveLoading.value = false;
      }
    }

    const showDeleteAccountConfirm = ref(false);
    const deleteAccountTarget = ref(null);
    const deleteAccountMessage = ref('');
    const deleteAccountLoading = ref(false);

    function handleDeleteAccount(account) {
      const count = bankTransactionStore.transactionCountByAccount(account.id);
      deleteAccountTarget.value = account;
      deleteAccountMessage.value = count > 0
        ? `A conta "${account.name}" tem ${count} transação(ões). Todas serão excluídas permanentemente. Esta ação não pode ser desfeita.`
        : `Tem certeza que deseja excluir "${account.name}"?`;
      showDeleteAccountConfirm.value = true;
    }

    async function doDeleteAccount() {
      if (!deleteAccountTarget.value) return;
      deleteAccountLoading.value = true;
      try {
        const accountId = deleteAccountTarget.value.id;
        const txQuery = query(collection(firestore, 'bankTransactions'), where('accountId', '==', accountId));
        const txSnap = await getDocs(txQuery);
        const docs = txSnap.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(firestore);
          docs.slice(i, i + 500).forEach((d) => batch.delete(d.ref));
          await batch.commit();
        }
        await deleteDoc(doc(firestore, 'bankAccounts', accountId));
        showDeleteAccountConfirm.value = false;
        deleteAccountTarget.value = null;
        showToast('Conta excluída.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir a conta.', 'error');
      } finally {
        deleteAccountLoading.value = false;
      }
    }

    // Transaction form
    const showTransactionModal = ref(false);
    const editingTransactionId = ref(null);
    const txAccountId = ref(null);
    const txDate = ref('');
    const txDirection = ref('in');
    const txAmount = ref(null);
    const txDescription = ref('');
    const txNotes = ref('');
    const txCategoryId = ref(null);
    const txAccountError = ref('');
    const txDateError = ref('');
    const txAmountError = ref('');
    const txDescriptionError = ref('');
    const txSaveLoading = ref(false);

    const txCategoryOptions = computed(() => {
      const kind = txDirection.value === 'in' ? 'income' : 'expense';
      return [
        { id: null, label: 'Nenhuma' },
        ...transactionCategoryStore.categoriesByKind(kind).map((c) => ({
          id: c.id,
          label: c.name,
        })),
      ];
    });

    function setTxDirection(direction) {
      txDirection.value = direction;
      if (!txCategoryId.value) return;
      const cat = transactionCategoryStore.transactionCategories[txCategoryId.value];
      const expectedKind = direction === 'in' ? 'income' : 'expense';
      if (cat && cat.kind !== expectedKind) {
        txCategoryId.value = null;
      }
    }

    function onCategorySelected(categoryId) {
      if (!categoryId) return;
      const cat = transactionCategoryStore.transactionCategories[categoryId];
      if (!cat) return;
      txDirection.value = cat.kind === 'income' ? 'in' : 'out';
    }

    function openAddTransaction() {
      editingTransactionId.value = null;
      txAccountId.value = accounts.value[0]?.id || null;
      txDate.value = balances.toDateKey(Date.now());
      txDirection.value = 'in';
      txAmount.value = null;
      txDescription.value = '';
      txNotes.value = '';
      txCategoryId.value = null;
      txAccountError.value = '';
      txDateError.value = '';
      txAmountError.value = '';
      txDescriptionError.value = '';
      showTransactionModal.value = true;
    }

    function openEditTransaction(tx) {
      editingTransactionId.value = tx.id;
      txAccountId.value = tx.accountId;
      txDate.value = balances.toDateKey(Number(tx.date) || Date.now());
      txDirection.value = Number(tx.amount) >= 0 ? 'in' : 'out';
      txAmount.value = Math.abs(Number(tx.amount) || 0);
      txDescription.value = tx.description || '';
      txNotes.value = tx.notes || '';
      txCategoryId.value = tx.categoryId || null;
      showTransactionModal.value = true;
    }

    function validateTransaction() {
      txAccountError.value = '';
      txDateError.value = '';
      txAmountError.value = '';
      txDescriptionError.value = '';
      let valid = true;
      if (!txAccountId.value) {
        txAccountError.value = 'Selecione a conta.';
        valid = false;
      }
      if (!txDate.value) {
        txDateError.value = 'Informe a data.';
        valid = false;
      }
      if (!txAmount.value || txAmount.value <= 0) {
        txAmountError.value = 'Informe um valor maior que zero.';
        valid = false;
      }
      if (!txDescription.value.trim()) {
        txDescriptionError.value = 'Informe a descrição.';
        valid = false;
      }
      return valid;
    }

    async function saveTransaction() {
      if (!validateTransaction()) return;
      txSaveLoading.value = true;
      try {
        const id = editingTransactionId.value || doc(collection(firestore, 'bankTransactions')).id;
        const existing = editingTransactionId.value
          ? bankTransactionStore.bankTransactions[editingTransactionId.value]
          : null;
        const signedAmount = roundMoney(
          txDirection.value === 'out' ? -Math.abs(txAmount.value) : Math.abs(txAmount.value)
        );
        const now = Date.now();
        const payload = {
          accountId: txAccountId.value,
          date: startOfLocalDay(new Date(txDate.value + 'T00:00:00').getTime()),
          amount: signedAmount,
          description: txDescription.value.trim(),
          notes: txNotes.value.trim() || '',
          categoryId: txCategoryId.value || null,
          source: existing?.source === 'ofx' ? 'ofx' : 'manual',
          updatedAt: now,
        };
        if (existing?.ofxFitId) payload.ofxFitId = existing.ofxFitId;
        if (existing?.ofxImportId) payload.ofxImportId = existing.ofxImportId;
        if (!editingTransactionId.value) {
          payload.source = 'manual';
          payload.createdAt = now;
          payload.createdBy = auth.currentUser?.uid || '';
        } else if (!existing?.createdAt) {
          payload.createdAt = now;
        }
        await setDoc(doc(firestore, 'bankTransactions', id), payload, { merge: true });
        showTransactionModal.value = false;
        showToast(editingTransactionId.value ? 'Transação alterada.' : 'Transação criada.', 'success');
      } catch (err) {
        showToast('Não foi possível salvar a transação.', 'error');
      } finally {
        txSaveLoading.value = false;
      }
    }

    const showDeleteTxConfirm = ref(false);
    const deleteTxTarget = ref(null);
    const deleteTxMessage = ref('');
    const deleteTxLoading = ref(false);

    function handleDeleteTransaction(tx) {
      deleteTxTarget.value = tx;
      deleteTxMessage.value = `Excluir "${tx.description}"?`;
      showDeleteTxConfirm.value = true;
    }

    async function doDeleteTransaction() {
      if (!deleteTxTarget.value) return;
      deleteTxLoading.value = true;
      try {
        await deleteDoc(doc(firestore, 'bankTransactions', deleteTxTarget.value.id));
        showDeleteTxConfirm.value = false;
        deleteTxTarget.value = null;
        showToast('Transação excluída.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir.', 'error');
      } finally {
        deleteTxLoading.value = false;
      }
    }

    // OFX import
    const showOfxWizard = ref(false);
    const ofxImportLoading = ref(false);

    async function handleOfxImport({ accountId, fileName, meta, transactions }) {
      ofxImportLoading.value = true;
      try {
        const functions = getFunctions(app, 'us-central1');
        const importBankTransactions = httpsCallable(functions, 'importBankTransactions');
        const result = await importBankTransactions({
          accountId,
          fileName,
          meta,
          transactions,
        });
        const data = result.data || {};
        showOfxWizard.value = false;
        showToast(
          `${data.importedCount || 0} transação(ões) importada(s)${data.skippedDuplicateCount ? `, ${data.skippedDuplicateCount} ignorada(s)` : ''}.`,
          'success'
        );
      } catch (err) {
        showToast(err.message || 'Não foi possível importar o arquivo.', 'error');
      } finally {
        ofxImportLoading.value = false;
      }
    }

    function formatBrl(n) {
      return Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function formatDateMs(ms) {
      const d = new Date(ms);
      const pad = (x) => (x < 10 ? '0' : '') + x;
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    }

    function getAccountName(id) {
      return bankAccountStore.bankAccounts[id]?.name || '—';
    }

    function getCategoryLabel(categoryId) {
      if (!categoryId) return UNCATEGORIZED_LABEL;
      return transactionCategoryStore.transactionCategories[categoryId]?.name || UNCATEGORIZED_LABEL;
    }

    function accountTypeLabel(type) {
      return BANK_ACCOUNT_TYPE_LABELS[type] || type;
    }

    function accountTypeIcon(type) {
      return getBankAccountTypeIcon(type);
    }

    return {
      tabs,
      activeTab,
      periodOptions,
      typeFilterOptions,
      periodRange,
      cashflowPeriodRange,
      customStartDate,
      customEndDate,
      accountFilter,
      cashflowAccountFilter,
      typeFilter,
      categoryFilter,
      categoryFilterOptions,
      categories,
      cashflowGranularity,
      balanceView,
      chartReady,
      accountFilterOptions,
      accountOptions,
      accountTypeOptions,
      accounts,
      allTransactions,
      filteredTransactions,
      periodSummary,
      cashflowRows,
      cashflowSeries,
      cashflowChartOptions,
      runningBalancePoints,
      runningBalanceSeries,
      runningBalanceChartOptions,
      selectedCashflowAccount,
      accountBalance: balances.accountBalance,
      filterByDateRange: balances.filterByDateRange,
      toDateKey: balances.toDateKey,
      showAccountModal,
      editingAccountId,
      accountNameInput,
      accountType,
      accountInstitution,
      accountLastFour,
      accountOpeningBalance,
      accountOpeningDate,
      accountNameError,
      accountOpeningBalanceError,
      accountOpeningDateError,
      accountSaveLoading,
      openAddAccount,
      openEditAccount,
      saveAccount,
      showDeleteAccountConfirm,
      deleteAccountMessage,
      deleteAccountLoading,
      handleDeleteAccount,
      doDeleteAccount,
      showTransactionModal,
      editingTransactionId,
      txAccountId,
      txDate,
      txDirection,
      txAmount,
      txDescription,
      txNotes,
      txCategoryId,
      txCategoryOptions,
      setTxDirection,
      onCategorySelected,
      txAccountError,
      txDateError,
      txAmountError,
      txDescriptionError,
      txSaveLoading,
      openAddTransaction,
      openEditTransaction,
      saveTransaction,
      showDeleteTxConfirm,
      deleteTxMessage,
      deleteTxLoading,
      handleDeleteTransaction,
      doDeleteTransaction,
      showOfxWizard,
      ofxImportLoading,
      handleOfxImport,
      formatBrl,
      formatDateMs,
      getAccountName,
      getCategoryLabel,
      accountTypeLabel,
      accountTypeIcon,
    };
  },
};
</script>

<style scoped>
.financeiro-page {
  padding: var(--space-md) 0;
}

.financeiro-tabs {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--space-xs);
  margin-bottom: var(--space-lg);
  border-bottom: 1px solid var(--color-border);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.financeiro-tabs::-webkit-scrollbar {
  display: none;
}

.financeiro-tab {
  background: none;
  border: none;
  padding: var(--space-sm) var(--space-md);
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  flex-shrink: 0;
  white-space: nowrap;
}

.financeiro-tab--active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.financeiro-filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
  align-items: center;
}

.financeiro-filters :deep(.v-select) {
  flex: 1 1 160px;
  min-width: 160px;
}

.financeiro-date-field {
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: inherit;
}

.financeiro-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
}

.financeiro-kpi-value {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.financeiro-kpi-in { background: #e8f5e9; color: #2e7d32; }
.financeiro-kpi-out { background: var(--color-error-bg); color: var(--color-error); }
.financeiro-kpi-net { background: var(--color-accent-soft); color: var(--color-accent); }

.financeiro-tx-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.financeiro-tx-card {
  grid-template-columns: auto 1fr auto auto;
  grid-template-areas: "icon content amount actions";
}

.financeiro-tx-card .financeiro-tx-amount {
  grid-area: amount;
}

.financeiro-tx-actions {
  flex-direction: row;
  gap: 2px;
}

.financeiro-tx-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.financeiro-tx-amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  align-self: center;
  text-align: right;
  white-space: nowrap;
}

.financeiro-tx-in { background: #e8f5e9; color: #2e7d32; }
.financeiro-tx-out { background: var(--color-error-bg); color: var(--color-error); }

.financeiro-source-badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: var(--color-border);
  margin-left: 4px;
}

.amount-in { color: #2e7d32; }
.amount-out { color: var(--color-error); }

.financeiro-toggle-group {
  display: inline-flex;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.financeiro-toggle-btn {
  background: var(--color-surface);
  border: none;
  padding: 8px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  color: var(--color-text-secondary);
}

.financeiro-toggle-btn--active {
  background: var(--color-accent-soft);
  color: var(--color-accent);
  font-weight: 600;
}

.financeiro-type-toggle {
  display: flex;
  gap: var(--space-xs);
}

.financeiro-chart-wrap,
.financeiro-running-balance,
.financeiro-summary-table-wrap {
  margin-bottom: var(--space-lg);
}

.financeiro-chart-container {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.financeiro-chart-container :deep(.apexcharts-canvas),
.financeiro-chart-container :deep(svg) {
  max-width: 100%;
}

.financeiro-subheading {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm);
  color: var(--color-text-secondary);
}

.financeiro-summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  table-layout: fixed;
}

.financeiro-summary-table th,
.financeiro-summary-table td {
  padding: var(--space-xs) var(--space-sm);
  border-bottom: 1px solid var(--color-border);
}

.financeiro-summary-table th.num,
.financeiro-summary-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.financeiro-account-icon {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}

.financeiro-account-balance {
  font-weight: 600;
}

.page-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
}

.btn-import-ofx {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  padding: 8px 14px;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-import-ofx .material-icons {
  font-size: 18px;
}

.empty-hint {
  text-align: center;
  color: var(--color-text-muted);
  margin: var(--space-xl) 0;
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
}

@media (max-width: 640px) {
  .financeiro-kpis {
    grid-template-columns: 1fr;
  }

  .financeiro-tabs {
    gap: 2px;
    margin-bottom: var(--space-md);
  }

  .financeiro-tab {
    padding: 5px 8px;
    font-size: 0.78rem;
    letter-spacing: -0.01em;
  }

  .financeiro-subheading {
    font-size: 0.8rem;
  }

  .financeiro-summary-table {
    font-size: 0.8125rem;
  }

  .financeiro-summary-table th,
  .financeiro-summary-table td {
    padding: var(--space-xs);
  }
}
</style>
